/**
 * Public (guest) call-booking server functions for the marketing site's
 * "Schedule a call" action. No authentication required — visitors pick a slot
 * from the team's real Google Calendar availability and provide their contact
 * details. Replaces the previous Calendly embed.
 *
 * Slots come from the system admin's linked calendar (Google free/busy + working
 * hours). Booking persists a guest `call_request`, creates a Google Calendar
 * event with the guest as an attendee, and emails both the team and the guest.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { safeOrigin } from "@/lib/safe-origin";
import type { DaySlots } from "@/lib/booking-slots.server";

/** Escapes user-controlled text before embedding it in HTML email bodies. */
const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

const SLOT_MINUTES = 30;
const BOOKING_WINDOW_DAYS = 30;
const LEAD_TIME_MS = 24 * 60 * 60 * 1000;

/**
 * Returns bookable call slots for the public site (system admin's calendar).
 */
export const getPublicSlots = createServerFn({ method: "POST" }).handler(async () => {
  const { resolveBookingConsultant, computeConsultantSlots } = await import(
    "@/lib/booking-slots.server"
  );
  const consultant = await resolveBookingConsultant();
  if (!consultant) {
    return { configured: false as const, timezone: "", days: [] as DaySlots[], consultantName: "" };
  }
  const { timezone, days } = await computeConsultantSlots(consultant.id);
  return { configured: true as const, timezone, days, consultantName: consultant.name };
});

/**
 * Books a public call slot: persists a guest call request, creates a calendar
 * event and notifies both parties.
 */
export const bookPublicCall = createServerFn({ method: "POST" })
  .inputValidator((data) =>
    z
      .object({
        start: z.string().datetime(),
        name: z.string().trim().min(1).max(120),
        email: z.string().trim().email().max(200),
        phone: z.string().trim().max(40).optional(),
        note: z.string().max(500).optional(),
        enquiryType: z.enum(["enquire", "client", "partner", "other"]).optional(),
        origin: z.string().url().optional(),
        // Honeypot — real users leave this empty; bots fill it in.
        website: z.string().max(200).optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    // Spam honeypot: pretend success but do nothing — no booking, no emails.
    if (data.website && data.website.trim()) {
      return { ok: true as const, scheduledAt: "", reference: "—", timezone: "" };
    }

    const startMs = new Date(data.start).getTime();
    if (Number.isNaN(startMs)) return { ok: false, reason: "invalid" as const };
    if (startMs < Date.now() + LEAD_TIME_MS) return { ok: false, reason: "too_soon" as const };
    if (startMs > Date.now() + (BOOKING_WINDOW_DAYS + 1) * 86_400_000)
      return { ok: false, reason: "out_of_window" as const };

    const endMs = startMs + SLOT_MINUTES * 60_000;
    const startIso = new Date(startMs).toISOString();
    const endIso = new Date(endMs).toISOString();

    const { resolveBookingConsultant } = await import("@/lib/booking-slots.server");
    const consultant = await resolveBookingConsultant();
    if (!consultant) return { ok: false, reason: "no_consultant" as const };
    const consultantId = consultant.id;

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Reject if the slot is already taken or now blocked by a calendar event.
    const { data: existing } = await supabaseAdmin
      .from("call_request")
      .select("id")
      .eq("consultant_id", consultantId)
      .eq("scheduled_at", startIso)
      .limit(1);
    if (existing && existing.length) return { ok: false, reason: "taken" as const };

    const { getConsultantBusy, createConsultantEvent } = await import(
      "@/lib/calendar-sources.server"
    );
    const busy = await getConsultantBusy(consultantId, startIso, endIso);
    if (busy.some((b) => startMs < new Date(b.end).getTime() && endMs > new Date(b.start).getTime()))
      return { ok: false, reason: "taken" as const };

    const { data: consultantProfile } = await supabaseAdmin
      .from("profiles")
      .select("name, email, phone, notification_prefs")
      .eq("id", consultantId)
      .maybeSingle();

    const { data: consultantAvail } = await supabaseAdmin
      .from("availability")
      .select("timezone")
      .eq("user_id", consultantId)
      .maybeSingle();

    const guestName = data.name.trim();
    const guestEmail = data.email.trim();
    const guestPhone = data.phone?.trim() || null;
    const note = data.note?.trim() || null;
    const timeZone = (consultantAvail?.timezone as string) || "Europe/London";

    const ENQUIRY_LABELS: Record<string, string> = {
      enquire: "Enquiry",
      client: "Existing client",
      partner: "Partner with us",
      other: "Other",
    };
    const enquiryType = data.enquiryType ?? null;
    const enquiryLabel = enquiryType ? ENQUIRY_LABELS[enquiryType] : null;

    const { data: inserted } = await supabaseAdmin
      .from("call_request")
      .insert({
        client_id: null,
        consultant_id: consultantId,
        client_name: guestName,
        client_email: guestEmail,
        client_phone: guestPhone,
        note,
        enquiry_type: enquiryType,
        status: "pending",
        scheduled_at: startIso,
      })
      .select("id")
      .maybeSingle();

    // Create the event on the consultant's own calendar (Google OAuth only).
    const eventId = await createConsultantEvent(consultantId, {
      summary: `Discovery call with ${guestName}`,
      description: `Booked via the Digitally Baffled website.\n\nName: ${guestName}\nEmail: ${guestEmail}${
        guestPhone ? `\nPhone: ${guestPhone}` : ""
      }${enquiryLabel ? `\nEnquiry type: ${enquiryLabel}` : ""}${note ? `\n\nNote: ${note}` : ""}`,
      startIso,
      endIso,
      timeZone,
      attendeeEmail: guestEmail,
    });
    if (eventId && inserted?.id) {
      await supabaseAdmin.from("call_request").update({ google_event_id: eventId }).eq("id", inserted.id);
    }

    // Notify the team + confirm to the guest (best-effort).
    const origin = safeOrigin(data.origin);
    const fmt = (ms: number) =>
      new Intl.DateTimeFormat("en-GB", {
        timeZone,
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
      }).format(new Date(ms));
    const when = fmt(startMs);

    try {
      const { sendEmail, brandedEmail } = await import("@/lib/email/resend.server");
      const prefs = (consultantProfile as { notification_prefs?: unknown } | null)?.notification_prefs;
      const allows = (channel: string) => {
        const p = prefs as Record<string, boolean> | null | undefined;
        return !p || p[channel] !== false;
      };

      if (consultantProfile?.email && allows("email")) {
        await sendEmail({
          to: consultantProfile.email,
          subject: `📅 New discovery call — ${guestName}, ${when}`,
          html: brandedEmail({
            heading: "A discovery call has been booked",
            intro: `<strong>${escapeHtml(guestName)}</strong> booked a 30-minute discovery call for <strong>${when}</strong> via the website.`,
            bodyHtml: `<strong>Email:</strong> ${escapeHtml(guestEmail)}${
              guestPhone ? `<br/><strong>Phone:</strong> ${escapeHtml(guestPhone)}` : ""
            }${
              enquiryLabel ? `<br/><strong>Enquiry type:</strong> ${enquiryLabel}` : ""
            }${
              note ? `<br/><br/>They added a note:<br/><em>“${escapeHtml(note)}”</em>` : ""
            }<br/><br/>It's been added to your calendar and the Call requests tab.`,
            ctaLabel: "Open the admin portal",
            ctaUrl: `${origin}/admin`,
          }),
          text: `${guestName} (${guestEmail}) booked a discovery call for ${when}.${
            note ? ` Note: ${note}` : ""
          } ${origin}/admin`,
        });
      }

      if (consultantProfile?.phone && allows("sms")) {
        const { sendSms } = await import("@/lib/sms.server");
        await sendSms(
          consultantProfile.phone,
          `📅 ${guestName} booked a discovery call for ${when} via the website (${guestEmail}).`,
        );
      }

      await sendEmail({
        to: guestEmail,
        subject: `Your discovery call is booked — ${when}`,
        html: brandedEmail({
          heading: "Your call is confirmed",
          intro: `Thanks ${escapeHtml(guestName)}! You're booked in for a 30-minute discovery call with the Digitally Baffled team.`,
          bodyHtml: `<strong>When:</strong> ${when} (${timeZone.replace(
            /_/g,
            " ",
          )})<br/><br/>We'll be in touch at the scheduled time. Need to change it? Just reply to this email.`,
          ctaLabel: "Visit our website",
          ctaUrl: origin,
        }),
        text: `Your discovery call with Digitally Baffled is booked for ${when}.`,
      });
    } catch (e) {
      console.error("[bookPublicCall] notify failed", e);
    }

    const reference = inserted?.id
      ? `DB-${inserted.id.replace(/-/g, "").slice(0, 6).toUpperCase()}`
      : `DB-${Date.now().toString(36).toUpperCase().slice(-6)}`;

    return { ok: true as const, scheduledAt: startIso, reference, timezone: timeZone };
  });
